import sanitizeHtml from 'sanitize-html'

import { RICH_TEXT_EDITOR_FILE_ID_ATTRIBUTE } from '../TextEditor/utils/imageAttributes'
import { canonicalizeTextEditorImageUrl } from '../TextEditor/utils/imageUrls'

export { isAllowedTextEditorImageUrl } from '../TextEditor/utils/imageUrls'

type Attributes = Record<string, string>

const CKEDITOR_IMAGE_CLASSES = new Set([
  'image',
  'image-inline',
  'image_resized',
  'image-style-align-left',
  'image-style-align-right',
  'image-style-block-align-left',
  'image-style-block-align-right',
  'image-style-side',
  // Keep as-is: the figure transform runs on ALL figures, and this is the
  // table-figure marker (`<figure class="table">`). We can't filter it out
  'table',
])

const IMAGE_TAGS = new Set(['figure', 'figcaption', 'picture', 'source', 'img'])

const sanitizeSrcset = (srcset: string) => {
  const candidates = srcset
    .split(',')
    .map((candidate) => candidate.trim())
    .filter(Boolean)

  if (candidates.length === 0) {
    return undefined
  }

  const sanitizedCandidates = candidates.map((candidate) => {
    const [url, ...descriptors] = candidate.split(/\s+/)
    const canonicalUrl = canonicalizeTextEditorImageUrl(url)

    if (
      !canonicalUrl ||
      descriptors.some(
        (descriptor) => !/^(?:\d+w|\d+(?:\.\d+)?x)$/.test(descriptor)
      )
    ) {
      return undefined
    }

    return [canonicalUrl, ...descriptors].join(' ')
  })

  if (sanitizedCandidates.includes(undefined)) {
    return undefined
  }

  return sanitizedCandidates.join(', ')
}

// Keep it as-is — we can't filter it out or the output loses the table's
// alignment and always falls back to the default (centered).
const TABLE_ALIGNMENT_CLASS = /^table-style-(block-)?align-(left|center|right)$/

const sanitizeImageClass = (className?: string) => {
  const allowedClasses = className
    ?.split(/\s+/)
    .filter(
      (name) =>
        CKEDITOR_IMAGE_CLASSES.has(name) || TABLE_ALIGNMENT_CLASS.test(name)
    )

  return allowedClasses?.length ? allowedClasses.join(' ') : undefined
}

const sanitizeImageSizes = (sizes?: string) => {
  if (!sizes || /url\(|javascript:|data:/i.test(sizes)) {
    return undefined
  }

  return /^[\w\s().,:+\-/%]+$/.test(sizes) ? sizes : undefined
}

const sanitizeProstoreFileId = (id?: string) =>
  id && /^[\w-]+$/.test(id) ? id : undefined

// ImageResize writes inline `width: X%` (and occasionally `height`) on the
// containing <figure>/<img>. Keep only those declarations so resized images
// render at the user's chosen size, `allowedStyles` further validates the values.
const SAFE_IMAGE_STYLE_PROPERTIES = new Set(['width', 'height', 'aspect-ratio'])

const filterImageStyle = (style?: string) => {
  if (!style) {
    return undefined
  }

  const safeDeclarations = style
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .filter((declaration) => {
      const [property] = declaration.split(':', 1)
      return SAFE_IMAGE_STYLE_PROPERTIES.has(
        property?.trim().toLowerCase() ?? ''
      )
    })

  return safeDeclarations.length ? `${safeDeclarations.join('; ')};` : undefined
}

const setOrDeleteAttribute = (
  attributes: Attributes,
  name: string,
  value?: string
) => {
  if (value) {
    attributes[name] = value
  } else {
    delete attributes[name]
  }
}

const sanitizeImageTagAttributes = (
  tagName: string,
  attributes: Attributes
) => {
  if (!IMAGE_TAGS.has(tagName)) {
    return
  }

  setOrDeleteAttribute(attributes, 'style', filterImageStyle(attributes.style))
  delete attributes.onclick
  delete attributes.onload
}

const sanitizeClassAttribute = (attributes: Attributes) => {
  setOrDeleteAttribute(
    attributes,
    'class',
    sanitizeImageClass(attributes.class)
  )
}

const sanitizeUrlAttribute = (
  attributes: Attributes,
  name: string,
  sanitize: (value: string) => string | undefined
) => {
  if (attributes[name]) {
    setOrDeleteAttribute(attributes, name, sanitize(attributes[name]))
  }
}

const sanitizeImageUrlAttributes = (attributes: Attributes) => {
  sanitizeUrlAttribute(attributes, 'src', canonicalizeTextEditorImageUrl)
  sanitizeUrlAttribute(attributes, 'srcset', sanitizeSrcset)
  sanitizeUrlAttribute(attributes, 'sizes', sanitizeImageSizes)
}

const sanitizeProstoreFileIdAttribute = (attributes: Attributes) => {
  setOrDeleteAttribute(
    attributes,
    RICH_TEXT_EDITOR_FILE_ID_ATTRIBUTE,
    sanitizeProstoreFileId(attributes[RICH_TEXT_EDITOR_FILE_ID_ATTRIBUTE])
  )
}

const addLazyImageLoadingAttributes = (
  tagName: string,
  attributes: Attributes
) => {
  if (tagName === 'img' && attributes.src) {
    attributes.loading = 'lazy'
    attributes.decoding = 'async'
  }
}

const transformImageTag = (tagName: string, attribs: Attributes) => {
  const sanitizedAttributes: Attributes = { ...attribs }

  sanitizeImageTagAttributes(tagName, sanitizedAttributes)
  sanitizeClassAttribute(sanitizedAttributes)
  sanitizeImageUrlAttributes(sanitizedAttributes)
  sanitizeProstoreFileIdAttribute(sanitizedAttributes)
  addLazyImageLoadingAttributes(tagName, sanitizedAttributes)

  return {
    tagName,
    attribs: sanitizedAttributes,
  }
}

export const sanitizeTextEditorHtml = (html: string) => {
  if (!html) {
    return ''
  }

  return sanitizeHtml(html, {
    allowedTags: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'span',
      'div',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'a',
      'figure',
      'figcaption',
      'table',
      'colgroup',
      'col',
      'thead',
      'tbody',
      'tr',
      'td',
      'th',
      'blockquote',
      'pre',
      'code',
      'hr',
      'img',
      'picture',
      'source',
      'sub',
      'sup',
    ],
    allowedAttributes: {
      '*': ['style', 'class'],
      a: ['href', 'title', 'target', 'rel'],
      figure: ['class', RICH_TEXT_EDITOR_FILE_ID_ATTRIBUTE],
      picture: ['class', RICH_TEXT_EDITOR_FILE_ID_ATTRIBUTE],
      source: ['srcset', 'sizes', 'type', 'media'],
      img: [
        'src',
        'srcset',
        'sizes',
        'alt',
        'title',
        'width',
        'height',
        'loading',
        'decoding',
        'class',
        RICH_TEXT_EDITOR_FILE_ID_ATTRIBUTE,
      ],
      table: ['border', 'cellpadding', 'cellspacing'],
      col: ['span'],
      td: ['colspan', 'rowspan'],
      th: ['colspan', 'rowspan', 'scope'],
    },
    allowedStyles: {
      '*': {
        color: [
          /^#[0-9a-fA-F]{3,6}$/,
          /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
          /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/,
        ],
        'background-color': [
          /^#[0-9a-fA-F]{3,6}$/,
          /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
          /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/,
        ],
        'font-size': [/^\d+(?:px|pt|em|rem|%)$/],
        'font-weight': [/^(?:normal|bold|bolder|lighter|\d{1,3})$/],
        'font-style': [/^(?:normal|italic|oblique)$/],
        'font-family': [/.*/],
        'text-decoration': [/^(?:none|underline|line-through|overline)$/],
        'text-align': [/^(?:left|right|center|justify)$/],
        'line-height': [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)?$/],
        width: [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/],
        'min-width': [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/],
        'max-width': [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/],
        height: [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/],
        'min-height': [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/],
        'max-height': [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/],
        margin: [/^\d+(?:px|pt|em|rem|%)?(?:\s+\d+(?:px|pt|em|rem|%)?)*$/],
        'margin-top': [/^\d+(?:px|pt|em|rem|%)?$/],
        'margin-bottom': [/^\d+(?:px|pt|em|rem|%)?$/],
        'margin-left': [/^\d+(?:px|pt|em|rem|%)?$/],
        'margin-right': [/^\d+(?:px|pt|em|rem|%)?$/],
        padding: [/^\d+(?:px|pt|em|rem|%)?(?:\s+\d+(?:px|pt|em|rem|%)?)*$/],
        'padding-top': [/^\d+(?:px|pt|em|rem|%)?$/],
        'padding-bottom': [/^\d+(?:px|pt|em|rem|%)?$/],
        'padding-left': [/^\d+(?:px|pt|em|rem|%)?$/],
        'padding-right': [/^\d+(?:px|pt|em|rem|%)?$/],
        border: [/.*/],
        'border-top': [/.*/],
        'border-bottom': [/.*/],
        'border-left': [/.*/],
        'border-right': [/.*/],
        'border-color': [
          /^#[0-9a-fA-F]{3,6}$/,
          /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/,
          /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/,
        ],
        'border-width': [/^\d+(?:px|pt|em|rem)?$/],
        'border-style': [/^(?:none|solid|dashed|dotted|double)$/],
        'list-style-type': [/.*/],
        'vertical-align': [/.*/],
        'white-space': [/.*/],
        'aspect-ratio': [/^\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?$/],
        float: [/^(?:left|right|none)$/],
      },
    },
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: {
      img: ['http', 'https'],
      source: ['http', 'https'],
    },
    transformTags: {
      figure: transformImageTag,
      figcaption: transformImageTag,
      picture: transformImageTag,
      source: transformImageTag,
      img: transformImageTag,
    },
    exclusiveFilter: (frame) =>
      (frame.tag === 'img' && !frame.attribs.src) ||
      (frame.tag === 'source' && !frame.attribs.srcset),
    allowedIframeHostnames: [],
  })
}
